import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError } from "../api/errors.js";

export const SLOW_START_DELAY_MS = 3_000;
export const WAKING_MESSAGE =
  "Waking up SafePlate… this can take up to 30 seconds";

const CURRENT_USER_MAX_RETRIES = 6;
const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

// A sleeping API answers 502/503/504 or drops the connection while it starts.
// Everything else (401, 403, 4xx, 500) is a real answer and is not retried.
export function shouldRetryCurrentUser(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= CURRENT_USER_MAX_RETRIES) return false;
  if (!(error instanceof ApiError)) return false;
  return error.code === "network" || RETRYABLE_STATUSES.has(error.status);
}

export function currentUserRetryDelay(failureCount: number): number {
  return Math.min(1_000 * 2 ** failureCount, 8_000);
}

export function watchSlowStart(
  onSlow: () => void,
  delayMs: number = SLOW_START_DELAY_MS,
): () => void {
  const handle = setTimeout(onSlow, delayMs);
  return () => clearTimeout(handle);
}

export function useSlowStart(
  active: boolean,
  delayMs: number = SLOW_START_DELAY_MS,
): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!active) {
      setSlow(false);
      return undefined;
    }
    return watchSlowStart(() => setSlow(true), delayMs);
  }, [active, delayMs]);
  return active && slow;
}

export function SlowStartNotice({ active }: { active: boolean }) {
  const slow = useSlowStart(active);
  if (!slow) return null;
  return (
    <p
      aria-live="polite"
      className="mt-4 flex items-center gap-2 text-sm font-medium text-stone-600"
      role="status"
    >
      <LoaderCircle
        aria-hidden="true"
        className="size-4 shrink-0 animate-spin text-emerald-800"
      />
      {WAKING_MESSAGE}
    </p>
  );
}
